import { Autocomplete, AutocompleteInputChangeReason } from '@mui/material';
import { ChangeEvent, FC, HTMLAttributes, SyntheticEvent, useCallback } from 'react';
import { SelectOption } from 'basics/options/options.types';
import ArrowDownIcon from 'components/Icons/ArrowDownIcon';
import FormHelperText from 'yoda-ui/components/Form/FormHelperText';
import { selectStyle, textFieldStyle } from 'yoda-ui/components/Form/SelectYoda/SelectYoda.styles';
import { SelectYodaInputProps, SortType } from 'yoda-ui/components/Form/SelectYoda/SelectYoda.types';
import TextField from 'yoda-ui/components/Form/TextField';
import Paper from 'yoda-ui/components/Paper';
import { findOptionById } from 'yoda-ui/utils/options.utils';
import { useYodaField } from 'yoda-ui/yodaForm';
import { isValidId } from 'yoda-ui/yodaForm/yodaForm.types';
import { YodaSpacing } from 'yoda-ui/yodaTheme';

const getSortedOptions = (options: SelectOption[], sortType: SortType = SortType.Alphabetical): SelectOption[] => {
  if (sortType === SortType.None) return options;
  return [...options].sort((a, b) => {
    if (a.label === 'All') return -1;
    if (b.label === 'All') return 1;
    if (sortType === SortType.Length) return a.label.length - b.label.length;
    return a.label.localeCompare(b.label);
  });
};

const SelectYoda: FC<SelectYodaInputProps> = ({
  onChange,
  defaultValue,
  label,
  name,
  freeOption = false,
  options,
  placeholder,
  disabled,
  disableClearable,
  required,
  validation,
  maxHeight,
  overflow,
  loading = false,
  loadingText = 'Loading...',
  clearOption = false,
  disableAutoComplete,
  sortType = SortType.Alphabetical,
  forcePopupIcon,
  iconColor,
}) => {
  const { onChangeFieldSynthetic,
    onChangeField,
    fieldValue,
    fieldDefaultValue,
    fieldShowError,
    fieldErrorMessage,
    setValue } = useYodaField({ name, defaultValue, onChange, validation });

  const findCurrentOptionById = useCallback((id: string | number): SelectOption | null => findOptionById(options, id), [options]);

  const handleInputChange = (event: SyntheticEvent<Element, Event>, _newInputValue: string,
    reason: AutocompleteInputChangeReason) => {
    if ((freeOption && reason === 'input')) {
      const changeEvent = event as ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;
      if (changeEvent.target.value === '') {
        setValue(name, null);
      } else {
        onChangeField(changeEvent);
      }
    }
  };

  const handleValue = () => {
    if ((freeOption && fieldValue) || clearOption) {
      return { id: fieldValue, label: fieldValue };
    }
    if (fieldValue && isValidId(fieldValue)) {
      return findCurrentOptionById(fieldValue);
    }
    return null;
  };

  const handleDefaultValue = () => {
    if (freeOption && fieldDefaultValue) {
      return { id: fieldDefaultValue, label: fieldDefaultValue };
    }
    if (fieldDefaultValue && isValidId(fieldDefaultValue)) {
      return findCurrentOptionById(fieldDefaultValue);
    }
    return null;
  };

  return (
    <>
      <Autocomplete
        data-testid="select-yoda"
        freeSolo={ freeOption }
        popupIcon={ <ArrowDownIcon style={ iconColor ? { color: iconColor } : undefined } /> }
        forcePopupIcon={ forcePopupIcon }
        PaperComponent={
          (props: HTMLAttributes<HTMLElement>) => (
            <Paper margin={ YodaSpacing.small } maxHeight={ maxHeight } overflow={ overflow }>{ props.children as React.ReactNode }</Paper>
          )
        }
        sx={ selectStyle }
        disabled={ disabled }
        disablePortal
        value={ handleValue() }
        id={ name }
        loading={ loading }
        loadingText={ loadingText }
        onInputChange={ handleInputChange }
        onChange={ onChangeFieldSynthetic }
        options={ getSortedOptions(options, sortType) }
        defaultValue={ handleDefaultValue() }
        disableClearable={ disableClearable }
        getOptionLabel={ (option) => (typeof option === 'string' ? option : option.label) }
        isOptionEqualToValue={ (option, value) => option.id === value.id }
        renderOption={
          (optionProps, option) => (
            <li { ...optionProps } key={ option.id }>{ option.label }</li>
          )
        }
        renderInput={
          (params) => (
            <TextField
              { ...params }
              sx={ textFieldStyle }
              label={ label }
              placeholder={ placeholder }
              required={ required }
              error={ fieldShowError }
              { ...(disableAutoComplete ? { inputProps: { ...params.inputProps, name: 'no-autofill' } } : {}) }
            />
          )
        }
      />
      { fieldShowError && <FormHelperText error>{ fieldErrorMessage }</FormHelperText> }
    </>
  );
};

export default SelectYoda;
